/**
 * Shared form fields for user create/edit modals.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

import type { UserRole } from '../../types'
import type { CreateUserForm, UserManagementOrganisation } from './types'

interface UserFormFieldsProps {
  formData: CreateUserForm
  setFormData: (data: CreateUserForm) => void
  isPlatformAdmin: boolean
  organisations: UserManagementOrganisation[]
  showCredentials: boolean
}

const TEXT_INPUT_CLASS =
  'w-full px-3 py-2 bg-white dark:bg-stone-900 border border-stone-300 dark:border-stone-600 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-[#4e5e82]'

const DISABLED_INPUT_CLASS =
  'w-full px-3 py-2 bg-stone-100 dark:bg-stone-900 border border-stone-300 dark:border-stone-600 rounded-lg text-stone-500'

const LABEL_CLASS = 'block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1'

function EmailField({
  formData,
  setFormData,
  showCredentials,
}: Pick<UserFormFieldsProps, 'formData' | 'setFormData' | 'showCredentials'>): React.ReactElement {
  return (
    <div>
      <label className={LABEL_CLASS}>Email</label>
      <input
        type="email"
        required={showCredentials}
        disabled={!showCredentials}
        value={formData.email}
        onChange={(e) => {
          setFormData({ ...formData, email: e.target.value })
        }}
        className={showCredentials ? TEXT_INPUT_CLASS : DISABLED_INPUT_CLASS}
      />
    </div>
  )
}

/**
 * No password input, deliberately. The server generates a one-time password and
 * returns it once, which `IssuedPasswordModal` then displays. A password field here
 * was worse than redundant: it was required, so an administrator had to invent one,
 * and the API discarded it — meaning the credential they passed to the new user had
 * never been set and could not work.
 */
function CredentialFields({
  formData,
  setFormData,
}: Pick<UserFormFieldsProps, 'formData' | 'setFormData'>): React.ReactElement {
  return (
    <div>
      <label className={LABEL_CLASS}>Username</label>
      <input
        type="text"
        required
        value={formData.username}
        onChange={(e) => {
          setFormData({ ...formData, username: e.target.value })
        }}
        className={TEXT_INPUT_CLASS}
      />
      <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
        A one-time password is generated on creation and shown to you once.
      </p>
    </div>
  )
}

function DisplayNameField({
  formData,
  setFormData,
}: Pick<UserFormFieldsProps, 'formData' | 'setFormData'>): React.ReactElement {
  return (
    <div>
      <label className={LABEL_CLASS}>Display Name</label>
      <input
        type="text"
        value={formData.displayName}
        onChange={(e) => {
          setFormData({ ...formData, displayName: e.target.value })
        }}
        className={TEXT_INPUT_CLASS}
      />
    </div>
  )
}

function RoleField({
  formData,
  setFormData,
  isPlatformAdmin,
}: Pick<UserFormFieldsProps, 'formData' | 'setFormData' | 'isPlatformAdmin'>): React.ReactElement {
  return (
    <div>
      <label className={LABEL_CLASS}>Role</label>
      <select
        value={formData.role}
        onChange={(e) => {
          setFormData({ ...formData, role: e.target.value as UserRole })
        }}
        className={TEXT_INPUT_CLASS}
      >
        {isPlatformAdmin && <option value="evidenx_admin">Platform Admin</option>}
        <option value="org_admin">Organisation Admin</option>
        <option value="investigator">Investigator</option>
        <option value="analyst">Analyst</option>
      </select>
    </div>
  )
}

function OrganisationField({
  formData,
  setFormData,
  organisations,
}: Pick<UserFormFieldsProps, 'formData' | 'setFormData' | 'organisations'>): React.ReactElement {
  return (
    <div>
      <label className={LABEL_CLASS}>Organisation</label>
      <select
        value={formData.organisationId}
        onChange={(e) => {
          setFormData({ ...formData, organisationId: e.target.value })
        }}
        className={TEXT_INPUT_CLASS}
      >
        <option value="">No organisation</option>
        {organisations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
    </div>
  )
}

export function UserFormFields({
  formData,
  setFormData,
  isPlatformAdmin,
  organisations,
  showCredentials,
}: UserFormFieldsProps): React.ReactElement {
  return (
    <>
      <EmailField formData={formData} setFormData={setFormData} showCredentials={showCredentials} />
      {showCredentials && <CredentialFields formData={formData} setFormData={setFormData} />}
      <DisplayNameField formData={formData} setFormData={setFormData} />
      <RoleField formData={formData} setFormData={setFormData} isPlatformAdmin={isPlatformAdmin} />
      {isPlatformAdmin && organisations.length > 0 && (
        <OrganisationField
          formData={formData}
          setFormData={setFormData}
          organisations={organisations}
        />
      )}
    </>
  )
}
